import { useMemo, useState } from "react";
import {
	Box,
	Paper,
	Typography,
	Switch,
	Chip,
	Button,
	IconButton,
	Tooltip,
	Divider,
	Stack,
	useTheme,
} from "@mui/material";
import {
	AddOutlined,
	DeleteOutline,
	NotificationsNoneOutlined,
	TuneOutlined,
} from "@mui/icons-material";
import DatagridComponent from "../../../datagridComponent";
import { ICONE_TIPO } from "../../iconeTipoAlerta";
import ModalExcecao from "../modalExcecao";
import ModalTipoAlerta from "../modalTipoAlerta";
import { SEVERIDADES, LABEL_SEVERIDADE } from "../../../../utils/centralDeAlertas/labels";
import { SINAL_SUBTRATIVO } from "../../../../utils/centralDeAlertas/regrasTipoAlerta";
import { corSeveridade } from "../../../../utils/centralDeAlertas/cores";

// O mapa de ícone por chave do catálogo (ICONE_TIPO) agora é compartilhado
// com as tabelas do painel — vive em components/centralDeAlertas/
// iconeTipoAlerta. Aqui o ícone identifica o tipo e a COR carrega a
// severidade (corSeveridade + rótulo no Tooltip) — um marcador só, sem chip.

// SEVERIDADES vem de labels.js já da mais grave para a menos grave; tipo sem
// severidade cai no fim da lista.
const posicaoSeveridade = (severidade) => {
	const i = SEVERIDADES.indexOf(severidade);
	return i === -1 ? SEVERIDADES.length : i;
};

// Aba "Exibição": padrão global por tipo (toggle) + exceções por cliente/host.
export default function AbaExibicao({
	tipos = [],
	excecoes = [],
	clientes = [],
	allHosts = [],
	onSalvarTipo,
	onSalvarExcecao,
	onRemoverExcecao,
	carregando,
}) {
	const theme = useTheme();
	const [modalAberto, setModalAberto] = useState(false);
	// Tipo em edição no modal de regras (null = fechado).
	const [tipoEmEdicao, setTipoEmEdicao] = useState(null);

	// Padrão global ordenado por severidade (mais grave primeiro); empate
	// desempata por nome. O backend devolve o catálogo em ordem alfabética.
	// "Não catalogados" (tipo especial, sem regras) fica SEMPRE por último —
	// é o balde do que sobra, não um tipo como os outros.
	const posicaoTipo = (t) =>
		t.chave === "nao_catalogado"
			? Number.MAX_SAFE_INTEGER
			: posicaoSeveridade(t.severidade);
	const tiposOrdenados = useMemo(
		() =>
			[...tipos].sort(
				(a, b) =>
					posicaoTipo(a) - posicaoTipo(b) ||
					String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
			),
		[tipos] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const colunas = [
		{ field: "tipoAlertaNome", headerName: "Tipo", flex: 1, minWidth: 140 },
		{
			field: "escopo",
			headerName: "Escopo",
			width: 110,
			renderCell: (p) => (
				<Chip
					size="small"
					label={p.value === "HOST" ? "Host" : "Cliente"}
					variant="outlined"
				/>
			),
		},
		{ field: "clienteNome", headerName: "Cliente", flex: 1, minWidth: 160 },
		{
			field: "hostId",
			headerName: "Host",
			width: 200,
			// x-data-grid v7: valueGetter é (value, row), não (params)
			valueGetter: (value) =>
				value
					? allHosts.find((h) => String(h?.hostid) === String(value))?.name ||
					  value
					: "—",
		},
		{
			field: "visivel",
			headerName: "Exibir",
			width: 90,
			renderCell: (p) => (
				<Chip
					size="small"
					color={p.value ? "success" : "default"}
					label={p.value ? "Sim" : "Não"}
				/>
			),
		},
		{
			field: "acoes",
			headerName: "",
			width: 60,
			sortable: false,
			renderCell: (p) => (
				<Tooltip title="Remover exceção">
					<IconButton
						size="small"
						onClick={() => onRemoverExcecao(p.row.id)}
					>
						<DeleteOutline fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box>
			{/* Padrão global */}
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
					Padrão global
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Quais tipos de alerta aparecem por padrão na Central. Use exceções
					abaixo para ligar/desligar em clientes ou hosts específicos; as
					regras de cada tipo são editadas pelo ícone de ajustes. "Não
					catalogados" controla os alertas que não casam nenhum tipo — e com
					TUDO desligado o painel mostra tudo (tela zerada não é pedido de
					silêncio).
				</Typography>
				<Stack divider={<Divider flexItem />} spacing={0}>
					{tiposOrdenados.map((t) => (
						<Box
							key={t.id}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 2,
								py: 1.25,
								px: 1,
								borderRadius: 2,
								transition: "background-color 120ms",
								"&:hover": { bgcolor: "action.hover" },
							}}
						>
							<Box sx={{ minWidth: 0 }}>
								<Stack direction="row" spacing={1} alignItems="center">
									{/* Ícone do tipo, na cor da severidade (cores.js, tenant +
									    modo; rótulo no Tooltip). Um marcador só faz os dois
									    papéis — o glifo diz o quê, a cor diz o quão grave — e
									    a ordenação da lista (mais grave primeiro) repete a
									    informação sem depender só da cor. */}
									{(() => {
										const Icone =
											ICONE_TIPO[t.chave] || NotificationsNoneOutlined;
										return (
											<Tooltip
												title={
													LABEL_SEVERIDADE[t.severidade] || t.severidade || ""
												}
											>
												<Icone
													sx={{
														fontSize: 18,
														flexShrink: 0,
														color: t.severidade
															? corSeveridade(theme, t.severidade)
															: "text.secondary",
													}}
												/>
											</Tooltip>
										);
									})()}
									<Typography sx={{ fontWeight: 500 }} noWrap>
										{t.nome}
									</Typography>
								</Stack>
								{t.descricao && (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block" }}
									>
										{t.descricao}
									</Typography>
								)}
								{/* As regras seguem à mostra (são elas que decidem o que é
								    deste tipo), como texto discreto — o sinal +/− distingue
								    incluir de excluir sem depender de cor; aditiva em verde
								    (soma) e subtrativa em vermelho (veto) reforçam. Detalhe
								    e edição ficam no modal (TuneOutlined). */}
								{t.regras?.length ? (
									<Typography
										variant="caption"
										component="div"
										sx={{
											fontFamily: "monospace",
											color: "text.secondary",
											opacity: 0.9,
											overflowWrap: "anywhere",
										}}
									>
										{(t.regras || []).map((regra, indice) => {
											const excluir = regra?.sinal === SINAL_SUBTRATIVO;
											return (
												<Box
													key={`${indice}-${regra?.padrao}`}
													component="span"
													sx={{
														mr: 1.5,
														whiteSpace: "nowrap",
														color: excluir ? "error.main" : "success.main",
													}}
												>
													{excluir ? "−" : "+"} {regra?.padrao}
												</Box>
											);
										})}
									</Typography>
								) : t.chave === "nao_catalogado" ? (
									// Tipo especial: sem regras POR DEFINIÇÃO — é o balde do
									// que não casa nada, não um tipo mal configurado.
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block" }}
									>
										Com tudo desligado, estes alertas também aparecem (o
										painel mostra tudo).
									</Typography>
								) : (
									<Typography
										variant="caption"
										color="error"
										sx={{ display: "block" }}
									>
										Sem regras — não casa alerta nenhum
									</Typography>
								)}
								{(t.severidadeMinima || t.chave === "host_down") && (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block" }}
									>
										{[
											t.severidadeMinima &&
												`Exibe de ${
													LABEL_SEVERIDADE[t.severidadeMinima] ||
													t.severidadeMinima
												} para pior`,
											// host_down é a exceção da falha recorrente: os
											// demais contam pela gravidade da trigger
											// (crítico/alto), este conta sempre.
											t.chave === "host_down" &&
												"Sempre conta como queda (ICMP/SNMP)",
										]
											.filter(Boolean)
											.join(" · ")}
									</Typography>
								)}
							</Box>
							<Stack
								direction="row"
								spacing={1.5}
								alignItems="center"
								sx={{ flexShrink: 0 }}
							>
								{/* O especial não tem regras para editar — só o switch. */}
								{t.chave !== "nao_catalogado" && (
									<Tooltip title="Editar regras e severidade mínima">
										<IconButton size="small" onClick={() => setTipoEmEdicao(t)}>
											<TuneOutlined fontSize="small" />
										</IconButton>
									</Tooltip>
								)}
								{/* O switch ganha um rótulo de estado: sem ele, "ligado"
								    não diz o quê está ligado. */}
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
									}}
								>
									<Switch
										size="small"
										checked={!!t.visivelPadrao}
										onChange={(e) =>
											onSalvarTipo({
												id: t.id,
												visivelPadrao: e.target.checked,
											})
										}
									/>
									<Typography
										variant="caption"
										color={t.visivelPadrao ? "text.primary" : "text.disabled"}
										sx={{ lineHeight: 1.2 }}
									>
										{t.visivelPadrao ? "Visível" : "Oculto"}
									</Typography>
								</Box>
							</Stack>
						</Box>
					))}
				</Stack>
			</Paper>

			{/* Exceções */}
			<Paper variant="outlined" sx={{ p: 2 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
					}}
				>
					<Box>
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							Exceções por cliente / host
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Precedência: host &gt; cliente &gt; padrão global.
						</Typography>
					</Box>
					<Button
						variant="contained"
						size="small"
						startIcon={<AddOutlined />}
						onClick={() => setModalAberto(true)}
					>
						Adicionar exceção
					</Button>
				</Box>

				<Box sx={{ height: 380, width: "100%" }}>
					<DatagridComponent
						rows={excecoes}
						columns={colunas}
						loading={carregando}
						isPagination
					/>
				</Box>
			</Paper>

			<ModalExcecao
				open={modalAberto}
				onClose={() => setModalAberto(false)}
				tipos={tipos}
				clientes={clientes}
				allHosts={allHosts}
				onSalvar={async (excecao) => {
					const ok = await onSalvarExcecao(excecao);
					if (ok !== false) setModalAberto(false);
				}}
			/>

			<ModalTipoAlerta
				open={!!tipoEmEdicao}
				tipo={tipoEmEdicao}
				onClose={() => setTipoEmEdicao(null)}
				onSalvar={async (payload) => {
					const ok = await onSalvarTipo(payload);
					if (ok !== false) setTipoEmEdicao(null);
				}}
			/>
		</Box>
	);
}
